import { useNavigate } from 'react-router';

import { useMemo } from 'react';

import {
  Typography,
  Modal,
  useModalContext,
  useModalWrapperContext,
  Button,
} from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';
import {
  ScenarioRoutePaths,
  useUserSimulationControl,
} from '@app-velocity/modules/scenarios';
import { TAiUnifiedReviewDto } from '@app-velocity/av-api-v2';

const isCorrectModalData = (
  data: unknown
): data is {
  workspaceId: string;
  teamId: string;
  shareToken: string;
  scenarioId: string;
  amount: number;
  type: TAiUnifiedReviewDto['type'];
  persona?: 'accessible' | 'default' | string;
  redirect?: boolean;
} => {
  const castedData = data as {
    workspaceId: string;
    teamId: string;
    shareToken: string;
    scenarioId: string;
    amount: number;
    type: TAiUnifiedReviewDto['type'];
    persona?: 'accessible' | 'default' | string;
    redirect?: boolean;
  };

  return (
    !!castedData.teamId &&
    !!castedData.workspaceId &&
    !!castedData.shareToken &&
    !!castedData.scenarioId &&
    !!castedData.type
  );
};

export const RunAnotherSimulationModalContent = () => {
  const { modalData, openedModalId } = useModalWrapperContext();

  const { closeModal } = useModalContext();
  const { start } = useUserSimulationControl();
  const navigate = useNavigate();
  const { t } = useI18n();

  const simulationData = useMemo(() => {
    const currentModalData = modalData[openedModalId];

    if (isCorrectModalData(currentModalData)) {
      return currentModalData;
    }

    return null;
  }, [modalData, openedModalId]);

  const runWithoutGenerating = () => {
    if (!simulationData) return;

    const { workspaceId, teamId, scenarioId, type, amount, redirect, persona } =
      simulationData;

    switch (type) {
      case 'SINGLE': {
        start(teamId, workspaceId, scenarioId, persona, 1, !!redirect);
        break;
      }

      case 'EXTENDED': {
        start(teamId, workspaceId, scenarioId, 'default', amount, !!redirect);
        break;
      }
    }

    closeModal();
  };

  const regenerateAndRun = () => {
    if (!simulationData) return;

    const { workspaceId, teamId } = simulationData;

    navigate(ScenarioRoutePaths.challengeAFlow(teamId, workspaceId));
    closeModal();
  };

  return (
    <Modal data-testid="run-another-simulation-modal">
      <Modal.Header title="prototypes:run_another_simulation.title" />
      <Modal.Content>
        <Typography variant="body2">
          {t('prototypes:run_another_simulation.text')}
        </Typography>
      </Modal.Content>
      <Modal.Controls>
        <Button
          variant="contained"
          color="secondary"
          onClick={regenerateAndRun}
        >
          {t('shared:yes')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={runWithoutGenerating}
        >
          {t('shared:no')}
        </Button>
      </Modal.Controls>
    </Modal>
  );
};
